import React from "react";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import { Label, Field, Input, Badge } from "@/components/ui";
import { FormSchemaType } from "../FormEditView";
import { Eye } from "lucide-react";
import { HandoffPreviewDialog } from "./HandoffPreviewDialog";

interface StarFieldControlsProps {
  idx: number;
  control: Control<FormSchemaType>;
  setValue: UseFormSetValue<FormSchemaType>;
  formFieldsValue: any;
}

export function StarFieldControls({ idx, control, setValue, formFieldsValue }: StarFieldControlsProps) {
  const collectionMode = formFieldsValue?.[idx]?.fieldOptions?.collectionMode || "hybrid";
  const handoffTiming = formFieldsValue?.[idx]?.fieldOptions?.handoffTiming || "on_completion";

  const showInternalTextPrompt = collectionMode === "first_party_only" || collectionMode === "hybrid";

  const [previewState, setPreviewState] = React.useState<{
    isOpen: boolean;
    handoffTiming: "on_completion" | "inline";
  }>({
    isOpen: false,
    handoffTiming: "on_completion",
  });

  const openPreview = (handoffTiming: "on_completion" | "inline") => {
    setPreviewState({
      isOpen: true,
      handoffTiming,
    });
  };

  return (
    <div className="mt-4 pt-4 space-y-6">
      {/* 1. Collection Mode Radio Cards */}
      <div className="space-y-3">
        <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
          Feedback Collection Mode
        </Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              value: "first_party_only",
              title: "First-Party Only",
              description:
                "Collect reviews privately. Feedback is recorded in SocialCoach without prompting for public sharing.",
            },
            {
              value: "third_party_only",
              title: "Third-Party Only",
              description:
                "Direct users straight to external public profiles (like Google or Zillow) to submit public reviews.",
            },
            {
              value: "hybrid",
              title: "Hybrid (Recommended)",
              description: "Blends private feedback collection and public reviews into a single, high-conversion flow.",
            },
          ].map(card => {
            const isSelected = collectionMode === card.value;
            return (
              <button
                key={card.value}
                type="button"
                onClick={() =>
                  setValue(`formFields.${idx}.fieldOptions.collectionMode`, card.value, { shouldDirty: true })
                }
                className={`flex flex-col text-left p-4 rounded-xl border-2 transition-all duration-200 outline-none focus:ring-2 focus:ring-primary/20 bg-white ${
                  isSelected ? "border-primary bg-primary/5 shadow-sm" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center justify-between w-full mb-1.5">
                  <span className={`font-sans font-bold text-sm ${isSelected ? "text-primary" : "text-slate-700"}`}>
                    {card.title}
                  </span>
                  <div
                    className={`size-3.5 rounded-full border flex items-center justify-center ${
                      isSelected ? "border-primary bg-primary" : "border-slate-300"
                    }`}
                  >
                    {isSelected && <div className="size-1 rounded-full bg-white" />}
                  </div>
                </div>
                <p className="font-sans text-sm text-muted-foreground leading-normal">{card.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Handoff Timing Radio Cards */}
      {collectionMode === "hybrid" && (
        <div className="space-y-3">
          <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Handoff Timing</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                value: "on_completion",
                title: "On Completion",
                description:
                  "Secures high-quality private feedback and internal ratings first. Once submitted, reviewers are prompted to post on public platforms, ensuring you always capture deep client insights.",
                pill: "Ensures Feedback First",
              },
              {
                value: "inline",
                title: "Smart Inline Handoff",
                description:
                  "Dynamically routes reviewers based on their star rating. Happy clients are immediately shown visual buttons to post on public sites (Google, Zillow, etc.), while clients with negative experiences are routed to private feedback first.",
                pill: "Highest Review Conversion",
              },
            ].map(card => {
              const isSelected = handoffTiming === card.value;
              return (
                <button
                  key={card.value}
                  type="button"
                  onClick={() =>
                    setValue(`formFields.${idx}.fieldOptions.handoffTiming`, card.value, { shouldDirty: true })
                  }
                  className={`flex flex-col text-left p-4 rounded-xl border-2 transition-all duration-200 outline-none focus:ring-2 focus:ring-primary/20 bg-white group ${
                    isSelected ? "border-primary bg-primary/5 shadow-sm" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-start justify-between w-full mb-1.5 gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`font-sans font-bold text-sm ${isSelected ? "text-primary" : "text-slate-700"}`}>
                        {card.title}
                      </span>
                      <Badge
                        variant={isSelected ? "default" : "secondary"}
                        className="text-xs px-2 py-0.5 font-semibold leading-none shrink-0"
                      >
                        {card.pill}
                      </Badge>
                    </div>
                    <div
                      className={`size-3.5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
                        isSelected ? "border-primary bg-primary" : "border-slate-300"
                      }`}
                    >
                      {isSelected && <div className="size-1 rounded-full bg-white" />}
                    </div>
                  </div>
                  <p className="font-sans text-sm text-muted-foreground leading-normal mb-3 flex-1">
                    {card.description}
                  </p>

                  <div className="pt-2.5 border-t border-slate-100 w-full flex justify-between items-center mt-auto">
                    <span className="text-xs text-slate-400 group-hover:text-slate-500 transition duration-150">
                      Want to see the client journey?
                    </span>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        openPreview(card.value as "on_completion" | "inline");
                      }}
                      className="text-xs text-primary hover:text-primary-hover font-bold hover:underline flex items-center gap-1 bg-primary/10 hover:bg-primary/15 px-2 py-1 rounded transition duration-150"
                    >
                      <Eye className="size-3" /> Preview Flow
                    </button>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 3. Conditional Prompt Inputs */}
      <div className="flex flex-col gap-6">
        {showInternalTextPrompt && (
          <Controller
            name={`formFields.${idx}.fieldOptions.internalTextPrompt`}
            control={control}
            defaultValue="Please share some details about your experience."
            render={({ field: promptField, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <Field.Label className="text-xs font-semibold text-slate-500">Feedback Request Prompt</Field.Label>
                <p className="text-xs text-muted-foreground mb-1.5 leading-normal">
                  Prompt text displayed as the heading above the optional detail comment field.
                </p>
                <Input
                  {...promptField}
                  placeholder="e.g. Tell us more about your experience with our team..."
                  className="bg-white text-xs"
                />
                {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
              </Field>
            )}
          />
        )}
      </div>

      {/* 4. Preview Dialog */}
      <HandoffPreviewDialog
        open={previewState.isOpen}
        onOpenChange={open => setPreviewState(prev => ({ ...prev, isOpen: open }))}
        handoffTiming={previewState.handoffTiming}
      />
    </div>
  );
}
